<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Badge from 'dashboard/components-next/badge/Badge.vue';

import { getLecturas, xmlToJson } from 'dashboard/api/cea';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

// State
const lecturas = ref([]);
const isLoading = ref(true);
const error = ref(null);
const selectedYear = ref('todos');
const availableYears = ref([]);

// Computed
const contratoId = computed(() => route.params.contratoId);
const explotacion = computed(() => route.params.explotacion);

const lecturasFiltradas = computed(() => {
  if (selectedYear.value === 'todos') {
    return lecturas.value;
  }
  return lecturas.value.filter(l => l.año === selectedYear.value);
});

const stats = computed(() => {
  const filtered = lecturasFiltradas.value;
  const consumos = filtered.map(l => l.consumo || 0).filter(c => c > 0);

  return {
    promedio:
      consumos.length > 0
        ? Math.round(consumos.reduce((sum, c) => sum + c, 0) / consumos.length)
        : 0,
    maximo: consumos.length > 0 ? Math.max(...consumos) : 0,
    minimo: consumos.length > 0 ? Math.min(...consumos) : 0,
    totalEstimadas: filtered.filter(l => l.estimado).length,
  };
});

const formatM3 = value => `${value} m³`;

// Datos para el gráfico - lecturas acumuladas
const chartData = computed(() => {
  const filtered = [...lecturasFiltradas.value]
    .sort((a, b) => {
      const dateA = new Date(a.fechaLectura);
      const dateB = new Date(b.fechaLectura);
      return dateB - dateA;
    })
    .reverse();

  const labels = filtered.map(l => {
    const periodo = l.periodo || '';
    const año = l.año || '';
    if (periodo.includes('-')) {
      return periodo;
    }
    const meses = [
      'ENE',
      'FEB',
      'MAR',
      'ABR',
      'MAY',
      'JUN',
      'JUL',
      'AGO',
      'SEP',
      'OCT',
      'NOV',
      'DIC',
    ];
    const mesIndex = parseInt(periodo, 10) - 1;
    if (mesIndex >= 0 && mesIndex < 12) {
      return `${meses[mesIndex]} ${año.slice(-2)}`;
    }
    return `${periodo} ${año}`;
  });

  return {
    labels,
    datasets: [
      {
        label: t('CONTRACTS.LECTURAS.READING_LABEL'),
        data: filtered.map(l => l.metrosCubicos),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };
});

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        usePointStyle: true,
        padding: 15,
        font: { family: 'Inter, sans-serif', size: 12 },
      },
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      padding: 12,
      titleFont: { size: 14, weight: 'bold' },
      bodyFont: { size: 12 },
      callbacks: {
        label: context => `${context.dataset.label}: ${context.parsed.y} m³`,
      },
    },
  },
  scales: {
    x: {
      display: true,
      grid: { display: true, drawBorder: false, color: 'rgba(0, 0, 0, 0.05)' },
      ticks: {
        maxRotation: 45,
        minRotation: 45,
        font: { family: 'Inter, sans-serif', size: 11 },
        color: '#64748b',
      },
    },
    y: {
      display: true,
      grid: { display: true, drawBorder: false, color: 'rgba(0, 0, 0, 0.05)' },
      ticks: {
        font: { family: 'Inter, sans-serif', size: 11 },
        color: '#64748b',
        callback: value => `${value} m³`,
      },
      title: {
        display: true,
        text: 'm³',
        font: { family: 'Inter, sans-serif', size: 12, weight: 'bold' },
        color: '#64748b',
      },
    },
  },
}));

// Methods
const fetchLecturas = async () => {
  if (!contratoId.value || !explotacion.value) {
    error.value = 'Faltan parámetros requeridos';
    isLoading.value = false;
    return;
  }

  isLoading.value = true;
  error.value = null;

  try {
    const explotacionFormatted = explotacion.value.padStart(2, '0');
    const xmlDoc = await getLecturas(
      explotacionFormatted,
      contratoId.value,
      'es'
    );
    const data = xmlToJson(xmlDoc);

    const lecturasRaw =
      data?.Body?.getLecturasResponse?.getLecturasReturn?.Lectura;

    if (!lecturasRaw) {
      throw new Error(
        `No se encontraron lecturas para el contrato con ese número de explotación ${explotacion.value}`
      );
    }

    const lecturasArray = Array.isArray(lecturasRaw)
      ? lecturasRaw
      : [lecturasRaw];

    const lecturasTransformadas = lecturasArray
      .sort(
        (a, b) =>
          new Date(a.fechaLectura).getTime() -
          new Date(b.fechaLectura).getTime()
      )
      .map((l, index, arr) => {
        const metrosCubicos = parseInt(l.metrosCubicos, 10) || 0;
        const anterior = index > 0 ? arr[index - 1] : null;
        const consumo = anterior
          ? metrosCubicos - (parseInt(anterior.metrosCubicos, 10) || 0)
          : 0;

        return {
          año: l.año,
          estimado: l.estimado === 'true',
          metrosCubicos,
          periodicidad: l.periodicidad,
          periodo: l.periodo?.replace(/<|>/g, '') || '',
          fechaLectura: l.fechaLectura,
          consumo: consumo > 0 ? consumo : 0,
        };
      })
      .sort(
        (a, b) =>
          new Date(b.fechaLectura).getTime() -
          new Date(a.fechaLectura).getTime()
      );

    lecturas.value = lecturasTransformadas;

    const years = [...new Set(lecturasTransformadas.map(l => l.año))].sort(
      (a, b) => parseInt(b, 10) - parseInt(a, 10)
    );
    availableYears.value = years;
  } catch (err) {
    error.value = `Error al cargar lecturas: ${err.message || 'Error desconocido'}`;
    useAlert(error.value);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchLecturas();
});
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0 bg-n-background">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <Spinner :size="32" />
        <h2 class="text-xl font-semibold mt-4">
          {{ $t('CONTRACTS.LECTURAS.LOADING') }}
        </h2>
        <p class="text-sm text-n-slate-11 mt-2">
          {{ $t('CONTRACTS.LECTURAS.LOADING_DESC', { id: contratoId }) }}
        </p>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error || lecturas.length === 0"
      class="flex items-center justify-center py-12 px-4"
    >
      <div class="text-center max-w-xl">
        <span class="i-lucide-alert-circle text-6xl text-n-ruby-11 mb-4" />
        <h2 class="text-xl font-semibold px-8">
          {{ $t('CONTRACTS.LECTURAS.NOT_FOUND') }}
        </h2>
        <p class="text-sm text-n-slate-11 mt-3 px-12">
          {{ error || $t('CONTRACTS.LECTURAS.NOT_FOUND') }}
        </p>
        <div class="flex gap-2 justify-center mt-6">
          <Button color="blue" @click="router.back()">
            {{ $t('CONTRACTS.BACK') }}
          </Button>
        </div>
      </div>
    </div>

    <!-- Lecturas Content -->
    <div v-else class="flex flex-col h-full overflow-hidden">
      <!-- Header -->
      <header class="flex items-center gap-4 p-4 border-b border-n-weak">
        <Button color="slate" size="small" @click="router.back()">
          <span class="i-lucide-arrow-left" />
        </Button>
        <div class="flex-1">
          <h1 class="text-2xl font-semibold text-n-slate-12">
            {{ $t('CONTRACTS.LECTURAS.HISTORY_TITLE') }}
          </h1>
          <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
          <p class="text-sm text-n-slate-11 mt-1">
            {{ $t('CONTRACTS.COMMON.CONTRACT_PREFIX') }} {{ contratoId }} |
            {{ $t('CONTRACTS.COMMON.EXPLOITATION_PREFIX') }} {{ explotacion }}
          </p>
        </div>
        <select
          v-model="selectedYear"
          class="px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12"
        >
          <option value="todos">{{ $t('CONTRACTS.COMMON.ALL_YEARS') }}</option>
          <option v-for="year in availableYears" :key="year" :value="year">
            {{ year }}
          </option>
        </select>
      </header>

      <!-- Content -->
      <div class="flex-1 overflow-auto p-4 space-y-6">
        <!-- Estadísticas -->
        <div class="grid gap-4 md:grid-cols-4">
          <div class="bg-n-background border border-n-weak rounded-lg p-4">
            <div class="flex items-center justify-between mb-2">
              <p class="text-xs text-n-slate-11">
                {{ $t('CONTRACTS.COMMON.MONTHLY_AVERAGE') }}
              </p>
              <span class="i-lucide-droplet text-n-slate-11" />
            </div>
            <p class="text-2xl font-bold text-n-slate-12">
              {{ formatM3(stats.promedio) }}
            </p>
            <p class="text-xs text-n-slate-10 mt-1">
              {{
                $t('CONTRACTS.LECTURAS.BASED_ON_READINGS', {
                  count: lecturasFiltradas.length,
                })
              }}
            </p>
          </div>
          <div class="bg-n-background border border-n-weak rounded-lg p-4">
            <div class="flex items-center justify-between mb-2">
              <p class="text-xs text-n-slate-11">
                {{ $t('CONTRACTS.COMMON.MAX_CONSUMPTION') }}
              </p>
              <span class="i-lucide-trending-up text-n-ruby-11" />
            </div>
            <p class="text-2xl font-bold text-n-slate-12">
              {{ formatM3(stats.maximo) }}
            </p>
            <p class="text-xs text-n-slate-10 mt-1">
              {{ $t('CONTRACTS.COMMON.PEAK_CONSUMPTION_RECORDED') }}
            </p>
          </div>
          <div class="bg-n-background border border-n-weak rounded-lg p-4">
            <div class="flex items-center justify-between mb-2">
              <p class="text-xs text-n-slate-11">
                {{ $t('CONTRACTS.COMMON.MIN_CONSUMPTION') }}
              </p>
              <span class="i-lucide-trending-down text-green-600" />
            </div>
            <p class="text-2xl font-bold text-n-slate-12">
              {{ formatM3(stats.minimo) }}
            </p>
            <p class="text-xs text-n-slate-10 mt-1">
              {{ $t('CONTRACTS.COMMON.LOWEST_CONSUMPTION_RECORDED') }}
            </p>
          </div>
          <div class="bg-n-background border border-n-weak rounded-lg p-4">
            <div class="flex items-center justify-between mb-2">
              <p class="text-xs text-n-slate-11">
                {{ $t('CONTRACTS.COMMON.ESTIMATED_READINGS') }}
              </p>
              <span class="i-lucide-bar-chart-3 text-n-slate-11" />
            </div>
            <p class="text-2xl font-bold text-n-slate-12">
              {{ stats.totalEstimadas }}
            </p>
            <p class="text-xs text-n-slate-10 mt-1">
              {{
                $t('CONTRACTS.LECTURAS.FROM_READINGS', {
                  count: lecturasFiltradas.length,
                })
              }}
            </p>
          </div>
        </div>

        <!-- Gráfica de Evolución de Lecturas -->
        <div class="bg-n-background border border-n-weak rounded-lg p-4">
          <h3
            class="text-lg font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
          >
            <span class="i-lucide-trending-up" />
            {{ $t('CONTRACTS.LECTURAS.READING_EVOLUTION') }}
          </h3>
          <div class="h-[300px] w-full">
            <Line :data="chartData" :options="chartOptions" />
          </div>
        </div>

        <!-- Tabla de Lecturas -->
        <div class="bg-n-background border border-n-weak rounded-lg p-4">
          <h3
            class="text-lg font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
          >
            <span class="i-lucide-droplet" />
            {{
              $t('CONTRACTS.LECTURAS.DETAIL_TITLE', {
                count: lecturasFiltradas.length,
              })
            }}
          </h3>
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="border-b border-n-weak">
                  <th
                    class="text-left py-2 px-3 text-xs font-semibold text-n-slate-11"
                  >
                    {{ $t('CONTRACTS.COMMON.PERIOD') }}
                  </th>
                  <th
                    class="text-left py-2 px-3 text-xs font-semibold text-n-slate-11"
                  >
                    {{ $t('CONTRACTS.COMMON.READING_DATE') }}
                  </th>
                  <th
                    class="text-right py-2 px-3 text-xs font-semibold text-n-slate-11"
                  >
                    {{ $t('CONTRACTS.COMMON.ACCUMULATED_READING') }}
                  </th>
                  <th
                    class="text-right py-2 px-3 text-xs font-semibold text-n-slate-11"
                  >
                    {{ $t('CONTRACTS.COMMON.MONTHLY_CONSUMPTION') }}
                  </th>
                  <th
                    class="text-center py-2 px-3 text-xs font-semibold text-n-slate-11"
                  >
                    {{ $t('CONTRACTS.COMMON.TYPE') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(lectura, index) in lecturasFiltradas"
                  :key="index"
                  class="border-b border-n-weak hover:bg-n-alpha-1"
                >
                  <td class="py-2 px-3 text-sm font-medium text-n-slate-12">
                    {{ lectura.periodo }} {{ lectura.año }}
                  </td>
                  <td class="py-2 px-3 text-sm text-n-slate-11">
                    {{
                      new Date(lectura.fechaLectura).toLocaleDateString(
                        'es-MX',
                        {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        }
                      )
                    }}
                  </td>
                  <td
                    class="py-2 px-3 text-sm text-right font-mono text-n-slate-12"
                  >
                    {{ formatM3(lectura.metrosCubicos.toLocaleString()) }}
                  </td>
                  <td
                    class="py-2 px-3 text-sm text-right font-mono font-semibold"
                  >
                    <span
                      v-if="lectura.consumo"
                      :class="
                        lectura.consumo > stats.promedio
                          ? 'text-n-ruby-11'
                          : 'text-green-600'
                      "
                    >
                      {{ formatM3(lectura.consumo.toLocaleString()) }}
                    </span>
                    <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                    <span v-else class="text-n-slate-10">-</span>
                  </td>
                  <td class="py-2 px-3 text-center">
                    <Badge
                      :color="lectura.estimado ? 'gray' : 'green'"
                      size="small"
                    >
                      {{
                        lectura.estimado
                          ? $t('CONTRACTS.COMMON.ESTIMATED')
                          : $t('CONTRACTS.COMMON.ACTUAL')
                      }}
                    </Badge>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
